import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import {
  Rocket, Sparkle, PaintBrush, BookOpen, Flag, VideoCamera, CheckCircle, ArrowRight,
  Palette, Package, IdentificationCard, Storefront, Coffee,
} from "@phosphor-icons/react";
import { SupportContactBand, SupportContactInline } from "@/components/SupportContact";

const setSEO = (title, desc) => {
  document.title = title;
  const q = (sel) => document.querySelector(sel);
  q('meta[name="description"]')?.setAttribute("content", desc);
  q('meta[property="og:title"]')?.setAttribute("content", title);
  q('meta[property="og:description"]')?.setAttribute("content", desc);
};

const SERVICES = [
  { icon: Sparkle,    title: "Logo design",         desc: "4 unique AI-generated concepts + 3 free revisions. Delivered as SVG, PNG, and favicon." },
  { icon: Palette,    title: "Brand identity kit",  desc: "Colour palette, font pairings, brand-voice guide, style-sheet PDF — everything a printer or designer needs downstream." },
  { icon: BookOpen,   title: "Print collateral",    desc: "Business cards, menu cards, brochures, flyers — pick what you need. Designed, printed, delivered." },
  { icon: Storefront, title: "Signage & shopfront", desc: "Hoardings, standees, wall art, uniforms — the physical presence your customers see first." },
  { icon: Package,    title: "Packaging",           desc: "Custom labels, boxes, stickers, tape — all print-ready with your new brand." },
  { icon: VideoCamera,title: "Brand video reel",    desc: "A 15-second AI-generated brand video for Instagram, YouTube Shorts, and WhatsApp status." },
];

const TIERS = [
  {
    name: "Bronze",
    price: "₹9,999",
    subtitle: "Perfect for a solo founder or tiny team",
    highlight: false,
    items: [
      "4 logo concepts + 3 revisions",
      "Brand identity kit (PDF)",
      "1 print item (business cards, or flyers, or labels)",
      "Delivered in 7 days",
    ],
    cta: "Start Bronze",
  },
  {
    name: "Silver",
    price: "₹19,999",
    subtitle: "Most popular — for a launching business",
    highlight: true,
    items: [
      "Everything in Bronze",
      "3 print items (mix and match)",
      "1 signage / hoarding artwork",
      "1 short brand video (15 seconds)",
      "Priority delivery in 5 days",
    ],
    cta: "Start Silver",
  },
  {
    name: "Gold",
    price: "₹49,999",
    subtitle: "Full brand launch — everything a physical shop needs",
    highlight: false,
    items: [
      "Everything in Silver",
      "6 print items (unlimited combos)",
      "Full shopfront signage suite",
      "Custom packaging kit (labels + boxes + tape)",
      "3 brand video reels",
      "Dedicated project manager",
    ],
    cta: "Start Gold",
  },
];

export default function StartupKit() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", business_name: "",
    business_type: "", stage: "idea", tier: "Silver",
    launch_date: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSEO(
      "Startup Launch Kit — Talaio/AI",
      "Everything a new Indian business needs to launch: logo, brand identity, print collateral, signage, and brand video — all in 7 days. Bronze / Silver / Gold packages from ₹9,999."
    );
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Reuses the existing vendor/designer application infrastructure — we tag it type=startup_kit
      // via the notes payload so admin can spot & follow up. For now goes into applications collection.
      const payload = {
        name: f.name, email: f.email, phone: f.phone || null,
        city: f.business_type || null,
        specialties: [f.tier + " Launch Kit"],
        hourly_rate: null,
        years_experience: null,
        portfolio_urls: [],
        bio: `[STARTUP-KIT LEAD]\nBusiness: ${f.business_name}\nType: ${f.business_type}\nStage: ${f.stage}\nTier: ${f.tier}\nLaunch date: ${f.launch_date || "TBD"}\nNotes: ${f.notes}`,
      };
      await api.post("/apply/designer", payload);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)]">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500 mb-6" />
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="startup-kit-success">You&apos;re on the list.</h1>
          <p className="text-lg text-[var(--pf-text-2)] mb-8">
            Thanks {f.name}. Our team will reach out to <strong>{f.email}</strong> within 24 hours with a launch plan for <strong>{f.business_name}</strong> and lock in your {f.tier} kit.
          </p>
          <Link to="/" className="pf-btn-secondary inline-block">← Back to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className="relative bg-[var(--pf-text)] text-white overflow-hidden">
        <div className="pf-cmyk-bar"><span/><span/><span/><span/></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="pf-label mb-4 inline-flex items-center gap-2 text-[var(--pf-brand)]">
              <Rocket size={14} weight="fill"/> Startup Launch Kit
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
              From logo to launch. <br/>
              <span className="text-[var(--pf-brand)]">In 7 days.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              A new business needs a logo, a menu, business cards, a shop signboard, packaging,
              and a video to post on Instagram — all matched, all consistent, all printed and delivered.
              We do that end-to-end.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#apply" data-testid="startup-hero-cta" className="inline-flex items-center justify-center gap-2 bg-[var(--pf-brand)] hover:bg-white hover:text-[var(--pf-text)] text-white px-6 py-3 font-bold rounded-lg transition-colors">
                Start my launch <ArrowRight size={14}/>
              </a>
              <a href="#pricing" className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-6 py-3 font-semibold rounded-lg transition-colors">
                See packages
              </a>
            </div>
            {/* Placement 1: hero — small "Talk to us first" line under CTAs */}
            <div className="mt-5">
              <SupportContactInline variant="hero" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Example — coffee shop */}
      <section className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3 inline-flex items-center gap-2"><Coffee size={14}/> Example</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4">Opening a coffee shop?</h2>
          <p className="text-base sm:text-lg text-[var(--pf-text-2)] max-w-3xl mb-10">
            Here&apos;s exactly what you get, in the exact order you need it — no chasing 4 different vendors.
          </p>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { i: Sparkle,           l: "Logo",         d: "Your café mark" },
              { i: Palette,           l: "Brand kit",    d: "Colours + fonts" },
              { i: BookOpen,          l: "Menu cards",   d: "Table + takeaway" },
              { i: IdentificationCard,l: "Biz cards",    d: "For you + team" },
              { i: Flag,              l: "Signage",      d: "Shop board + inside" },
              { i: VideoCamera,       l: "Brand reel",   d: "15-sec Instagram" },
            ].map(({ i: Ic, l, d }, idx) => (
              <motion.div key={l} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }}
                className="border border-[var(--pf-border)] p-4 flex flex-col items-start gap-2 bg-[var(--pf-bg)] hover:border-[var(--pf-brand)] transition-colors">
                <Ic size={22} weight="duotone" className="text-[var(--pf-brand)]" />
                <div className="font-black text-sm">{l}</div>
                <div className="text-xs text-[var(--pf-text-2)]">{d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3">What&apos;s included</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-10">Six services, one team.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map(({ icon: Ic, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="border border-[var(--pf-border)] bg-white p-6">
                <Ic size={28} className="text-[var(--pf-brand)] mb-3" weight="duotone" />
                <h3 className="font-black text-xl tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3">Packages</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3">Pick your kit.</h2>
          <p className="text-sm text-[var(--pf-text-3)] mb-10">Indicative pricing — we&apos;ll confirm exact scope on our intro call.</p>

          <div className="grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <div key={t.name}
                className={`border-2 p-6 flex flex-col ${t.highlight ? "border-[var(--pf-brand)] bg-[var(--pf-brand)]/5 shadow-lg relative" : "border-[var(--pf-border)] bg-white"}`}
                data-testid={`tier-${t.name.toLowerCase()}`}>
                {t.highlight && (
                  <div className="absolute -top-3 left-6 bg-[var(--pf-brand)] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">Most popular</div>
                )}
                <div className="pf-label mb-2">{t.name}</div>
                <div className="text-4xl font-black tracking-tighter">{t.price}</div>
                <div className="text-sm text-[var(--pf-text-2)] mt-2 mb-6">{t.subtitle}</div>
                <ul className="space-y-2 flex-1">
                  {t.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm">
                      <CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => { setF({ ...f, tier: t.name }); document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`tier-cta-${t.name.toLowerCase()}`}
                  className={`mt-6 py-3 px-5 font-bold rounded-lg transition-colors ${t.highlight ? "bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white" : "border-2 border-[var(--pf-text)] hover:bg-[var(--pf-text)] hover:text-white text-[var(--pf-text)]"}`}
                >
                  {t.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Placement 2: full contact band — between Pricing and Apply form */}
      <SupportContactBand />

      {/* Apply */}
      <section id="apply" className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="pf-label mb-3">Get started</div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3">Tell us about your business.</h2>
        <p className="text-[var(--pf-text-2)] mb-10">30-second form. We&apos;ll call you within 24 hours to lock the scope + timeline.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="startup-kit-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="pf-label block mb-2">Your name *</label>
              <input required data-testid="sk-name" className="pf-input" value={f.name} onChange={upd("name")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Business name *</label>
              <input required data-testid="sk-business" className="pf-input" value={f.business_name} onChange={upd("business_name")} placeholder="Even if it's still tentative" />
            </div>
            <div>
              <label className="pf-label block mb-2">Email *</label>
              <input required type="email" data-testid="sk-email" className="pf-input" value={f.email} onChange={upd("email")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Phone / WhatsApp</label>
              <input data-testid="sk-phone" placeholder="+91 …" className="pf-input" value={f.phone} onChange={upd("phone")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Business type *</label>
              <input required data-testid="sk-type" placeholder="e.g. coffee shop, D2C skincare, salon, tuition centre" className="pf-input" value={f.business_type} onChange={upd("business_type")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Stage</label>
              <select data-testid="sk-stage" className="pf-input" value={f.stage} onChange={upd("stage")}>
                <option value="idea">Idea stage — no logo, no anything</option>
                <option value="planning">Planning to launch in 1–3 months</option>
                <option value="soft-launch">Soft launched, need to look pro</option>
                <option value="running">Already running, rebranding</option>
              </select>
            </div>
            <div>
              <label className="pf-label block mb-2">Preferred package</label>
              <select data-testid="sk-tier" className="pf-input" value={f.tier} onChange={upd("tier")}>
                <option value="Bronze">Bronze — ₹9,999</option>
                <option value="Silver">Silver — ₹19,999</option>
                <option value="Gold">Gold — ₹49,999</option>
                <option value="Not sure">Not sure — you recommend</option>
              </select>
            </div>
            <div>
              <label className="pf-label block mb-2">Target launch date</label>
              <input type="date" data-testid="sk-launch" className="pf-input" value={f.launch_date} onChange={upd("launch_date")} />
            </div>
          </div>
          <div>
            <label className="pf-label block mb-2">Anything specific we should know? (optional)</label>
            <textarea data-testid="sk-notes" rows={3} placeholder="Colour preferences, competitor brands you like, whatever comes to mind…" className="pf-input resize-none" value={f.notes} onChange={upd("notes")} />
          </div>

          <button type="submit" disabled={loading} data-testid="sk-submit" className="pf-btn-primary w-full sm:w-auto mt-4 justify-center">
            {loading ? "Sending…" : "Start my launch →"}
          </button>
          <p className="text-xs text-[var(--pf-text-3)]">
            By submitting you agree to be contacted about your project. We don&apos;t spam.
          </p>

          {/* Placement 3: inline contact under apply form — safety net */}
          <div className="mt-6 pt-6 border-t border-[var(--pf-border)]">
            <SupportContactInline variant="form" />
          </div>
        </form>
      </section>
    </div>
  );
}
